import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Star, Award, TrendingUp, Users, Sparkles,
  Crown, Target, BarChart3, Heart, Zap, ChevronUp, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TierBadge = ({ tier }) => {
  const config = {
    gold: { bg: 'bg-gradient-to-r from-amber-400 to-yellow-500', text: 'text-white', icon: Crown },
    silver: { bg: 'bg-gradient-to-r from-gray-300 to-gray-400', text: 'text-white', icon: Medal },
    bronze: { bg: 'bg-gradient-to-r from-orange-400 to-orange-500', text: 'text-white', icon: Medal },
    rising: { bg: 'bg-gradient-to-r from-purple-400 to-pink-500', text: 'text-white', icon: Sparkles }
  };
  const cfg = config[tier] || config.rising;
  const Icon = cfg.icon;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium capitalize">{tier} Performer</span>
    </div>
  );
};

const BadgeIcon = ({ badge }) => {
  const icons = {
    star: '⭐',
    medal: '🏅',
    trophy: '🏆',
    crown: '👑',
    heart: '❤️',
    sparkle: '✨'
  };
  return <span className="text-xl">{icons[badge] || '🌟'}</span>;
};

const TherapistPerformanceTab = ({ therapistId, therapistName, propertyId }) => {
  const [performance, setPerformance] = useState(null);
  const [spaLeaderboard, setSpaLeaderboard] = useState(null);
  const [companyLeaderboard, setCompanyLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapistId, propertyId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const requests = [
        axios.get(`${API}/therapist/${therapistId}/performance`, { headers }),
        axios.get(`${API}/leaderboard/company`, { headers })
      ];
      
      if (propertyId) {
        requests.push(axios.get(`${API}/leaderboard/spa/${propertyId}`, { headers }));
      }
      
      const responses = await Promise.all(requests);
      
      setPerformance(responses[0].data);
      setCompanyLeaderboard(responses[1].data);
      if (responses[2]) {
        setSpaLeaderboard(responses[2].data);
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMyRank = (leaderboard) => {
    if (!leaderboard?.leaderboard) return null;
    return leaderboard.leaderboard.find(t => t.therapist_id === therapistId);
  };

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Loading performance data...</div>;
  }

  const mySpRank = getMyRank(spaLeaderboard);
  const myCompanyRank = getMyRank(companyLeaderboard);

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="flex gap-2">
        <Button 
          variant={activeView === 'overview' ? 'default' : 'ghost'}
          onClick={() => setActiveView('overview')}
        >
          <BarChart3 className="w-4 h-4 mr-2" /> My Performance
        </Button>
        <Button 
          variant={activeView === 'spa' ? 'default' : 'ghost'}
          onClick={() => setActiveView('spa')}
        >
          <Users className="w-4 h-4 mr-2" /> Spa Rankings
        </Button>
        <Button 
          variant={activeView === 'company' ? 'default' : 'ghost'}
          onClick={() => setActiveView('company')}
        >
          <Trophy className="w-4 h-4 mr-2" /> Company Rankings
        </Button>
      </div>

      {/* Overview Tab */}
      {activeView === 'overview' && performance && (
        <div className="space-y-6">
          {/* Hero Card - Performance Score */}
          <Card className="glass overflow-hidden">
            <div className={`p-6 ${
              performance.tier === 'gold' ? 'bg-gradient-to-r from-amber-50 to-yellow-50' :
              performance.tier === 'silver' ? 'bg-gradient-to-r from-gray-50 to-slate-50' :
              performance.tier === 'bronze' ? 'bg-gradient-to-r from-orange-50 to-amber-50' :
              'bg-gradient-to-r from-purple-50 to-pink-50'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <TierBadge tier={performance.tier} />
                  <h2 className="text-3xl font-serif font-bold mt-3">{performance.therapist_name}</h2>
                  <p className="text-muted-foreground">Performance Dashboard</p>
                </div>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center">
                    <div>
                      <p className="text-3xl font-bold">{performance.performance_score}</p>
                      <p className="text-xs text-muted-foreground">Score</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Score Breakdown */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg font-serif">Score Breakdown</CardTitle>
              <CardDescription>How your performance score is calculated</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Services Completed</span>
                  <span className="font-medium">{performance.score_breakdown?.services || 0}/30</span>
                </div>
                <Progress value={(performance.score_breakdown?.services || 0) / 30 * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Customer Ratings</span>
                  <span className="font-medium">{performance.score_breakdown?.ratings || 0}/30</span>
                </div>
                <Progress value={(performance.score_breakdown?.ratings || 0) / 30 * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Positive Feedback</span>
                  <span className="font-medium">{performance.score_breakdown?.positive_feedback || 0}/20</span>
                </div>
                <Progress value={(performance.score_breakdown?.positive_feedback || 0) / 20 * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Admin Appreciations</span>
                  <span className="font-medium">{performance.score_breakdown?.appreciations || 0}/20</span>
                </div>
                <Progress value={(performance.score_breakdown?.appreciations || 0) / 20 * 100} className="h-2" />
              </div>
            </CardContent>
          </Card>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="glass">
              <CardContent className="pt-4 text-center">
                <Target className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold">{performance.metrics?.total_services || 0}</p>
                <p className="text-xs text-muted-foreground">Total Services</p>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardContent className="pt-4 text-center">
                <Star className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{performance.metrics?.average_rating || 0}</p>
                <p className="text-xs text-muted-foreground">Avg Rating</p>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardContent className="pt-4 text-center">
                <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold">{performance.metrics?.positive_feedback || 0}</p>
                <p className="text-xs text-muted-foreground">Positive Reviews</p>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardContent className="pt-4 text-center">
                <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold">{performance.metrics?.appreciation_count || 0}</p>
                <p className="text-xs text-muted-foreground">Appreciations</p>
              </CardContent>
            </Card>
          </div>

          {/* My Rankings */}
          <div className="grid md:grid-cols-2 gap-4">
            {mySpRank && (
              <Card className="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-serif flex items-center gap-2">
                    <Users className="w-5 h-5" /> Spa Ranking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      mySpRank.rank === 1 ? 'bg-amber-400 text-white' :
                      mySpRank.rank === 2 ? 'bg-gray-400 text-white' :
                      mySpRank.rank === 3 ? 'bg-orange-400 text-white' :
                      'bg-muted'
                    }`}>
                      {mySpRank.rank === 1 ? <Trophy className="w-8 h-8" /> :
                       <span className="text-2xl font-bold">#{mySpRank.rank}</span>}
                    </div>
                    <div>
                      <p className="text-xl font-bold">Rank #{mySpRank.rank}</p>
                      <p className="text-sm text-muted-foreground">in your spa</p>
                      {mySpRank.rank === 1 && (
                        <Badge className="mt-1 bg-amber-100 text-amber-700">Top Performer!</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {myCompanyRank && (
              <Card className="glass">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-serif flex items-center gap-2">
                    <Trophy className="w-5 h-5" /> Company Ranking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      myCompanyRank.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' :
                      myCompanyRank.rank <= 5 ? 'bg-purple-500 text-white' :
                      myCompanyRank.rank <= 10 ? 'bg-blue-500 text-white' :
                      'bg-muted'
                    }`}>
                      {myCompanyRank.rank === 1 ? <Crown className="w-8 h-8" /> :
                       <span className="text-2xl font-bold">#{myCompanyRank.rank}</span>}
                    </div>
                    <div>
                      <p className="text-xl font-bold">Rank #{myCompanyRank.rank}</p>
                      <p className="text-sm text-muted-foreground">across all spas</p>
                      {myCompanyRank.rank === 1 && (
                        <Badge className="mt-1 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700">
                          Company Champion!
                        </Badge>
                      )}
                      {myCompanyRank.rank <= 5 && myCompanyRank.rank > 1 && (
                        <Badge className="mt-1 bg-purple-100 text-purple-700">Top 5!</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Recent Appreciations */}
          {performance.recent_appreciations?.length > 0 && (
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" /> Recent Appreciations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {performance.recent_appreciations.map((ap, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                      <BadgeIcon badge={ap.badge} />
                      <div className="flex-1">
                        <p className="font-medium capitalize">{ap.type?.replace('_', ' ')}</p>
                        <p className="text-xs text-muted-foreground">From {ap.from} • {ap.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Level Up Tips */}
          <Card className="glass bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" /> Level Up Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {performance.performance_score < 80 && (
                  <li className="flex items-start gap-2">
                    <ChevronUp className="w-4 h-4 text-blue-600 mt-0.5" />
                    <span>Complete more services this month to boost your score</span>
                  </li>
                )}
                {(performance.metrics?.average_rating || 0) < 4.5 && (
                  <li className="flex items-start gap-2">
                    <ChevronUp className="w-4 h-4 text-blue-600 mt-0.5" />
                    <span>Focus on customer satisfaction to improve your average rating</span>
                  </li>
                )}
                {(performance.metrics?.appreciation_count || 0) < 3 && (
                  <li className="flex items-start gap-2">
                    <ChevronUp className="w-4 h-4 text-blue-600 mt-0.5" />
                    <span>Consistent excellence leads to more admin appreciations</span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <ChevronUp className="w-4 h-4 text-blue-600 mt-0.5" />
                  <span>Encourage customers to leave detailed positive feedback</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Spa Leaderboard */}
      {activeView === 'spa' && (
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-lg font-serif">Spa Leaderboard</CardTitle>
            <CardDescription>Rankings within your spa</CardDescription>
          </CardHeader>
          <CardContent>
            {spaLeaderboard?.leaderboard?.length > 0 ? (
              <div className="space-y-2">
                {spaLeaderboard.leaderboard.map((t, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      t.therapist_id === therapistId ? 'bg-primary/10 border-2 border-primary' :
                      idx === 0 ? 'bg-amber-50 border border-amber-200' :
                      idx === 1 ? 'bg-gray-50 border border-gray-200' :
                      idx === 2 ? 'bg-orange-50/50 border border-orange-200' :
                      'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-amber-400 text-white' :
                        idx === 1 ? 'bg-gray-400 text-white' :
                        idx === 2 ? 'bg-orange-400 text-white' :
                        'bg-muted'
                      }`}>
                        {idx === 0 ? <Trophy className="w-5 h-5" /> :
                         idx === 1 || idx === 2 ? <Medal className="w-5 h-5" /> :
                         <span className="font-bold">{t.rank}</span>}
                      </div>
                      <div>
                        <p className="font-medium">
                          {t.name}
                          {t.therapist_id === therapistId && <span className="text-primary ml-2">(You)</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-bold">{t.score}</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                      <div className="text-center">
                        <p className="flex items-center gap-1">
                          {t.average_rating} <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        </p>
                        <p className="text-xs text-muted-foreground">Rating</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">No ranking data available</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Company Leaderboard */}
      {activeView === 'company' && (
        <div className="space-y-4">
          {/* Champion Highlight */}
          {companyLeaderboard?.champion && (
            <Card className="glass bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                    <Trophy className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-amber-600 font-medium">COMPANY CHAMPION</p>
                    <p className="text-xl font-serif font-bold">{companyLeaderboard.champion.name}</p>
                    <p className="text-sm text-muted-foreground">{companyLeaderboard.champion.property}</p>
                  </div>
                  <div className="ml-auto">
                    <Badge className="bg-amber-100 text-amber-700">Score: {companyLeaderboard.champion.score}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg font-serif">Company Rankings</CardTitle>
              <CardDescription>Top performers across all spas</CardDescription>
            </CardHeader>
            <CardContent>
              {companyLeaderboard?.leaderboard?.length > 0 ? (
                <div className="space-y-2">
                  {companyLeaderboard.leaderboard.map((t, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        t.therapist_id === therapistId ? 'bg-primary/10 border-2 border-primary' :
                        idx === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' :
                        idx === 1 ? 'bg-gray-50 border border-gray-200' :
                        idx === 2 ? 'bg-orange-50/50 border border-orange-200' :
                        'bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          idx === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' :
                          idx === 1 ? 'bg-gray-400 text-white' :
                          idx === 2 ? 'bg-orange-400 text-white' :
                          idx < 5 ? 'bg-purple-400 text-white' :
                          'bg-muted'
                        }`}>
                          {idx === 0 ? <Crown className="w-5 h-5" /> :
                           idx <= 2 ? <Medal className="w-5 h-5" /> :
                           <span className="font-bold text-sm">{t.rank}</span>}
                        </div>
                        <div>
                          <p className="font-medium">
                            {t.name}
                            {t.therapist_id === therapistId && <span className="text-primary ml-2">(You)</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{t.property}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="font-bold">{t.score}</p>
                          <p className="text-xs text-muted-foreground">Score</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">{t.total_services}</p>
                          <p className="text-xs text-muted-foreground">Services</p>
                        </div>
                        <div className="text-center">
                          <p className="flex items-center gap-1">
                            {t.average_rating} <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          </p>
                          <p className="text-xs text-muted-foreground">Rating</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No ranking data available</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TherapistPerformanceTab;
